// Attribute section for System Architecture - 180 lines ✅
import { Tag } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

const d365Versions = [
  '10.0.37', '10.0.38', '10.0.39', '10.0.40',
  '10.0.41', '10.0.42', '10.0.43', '10.0.44',
  '10.0.45', '10.0.46'
]

const d365Modules = [
  { name: 'Finance', description: 'Financial management and accounting' },
  { name: 'Supply Chain Management', description: 'SCM operations and logistics' },
  { name: 'Commerce', description: 'Retail and e-commerce operations' },
  { name: 'Human Resources', description: 'HR management and payroll' },
  { name: 'Project Operations', description: 'Project-based services' },
  { name: 'Manufacturing', description: 'Production and manufacturing' },
  { name: 'Warehouse Management', description: 'Warehouse operations' },
  { name: 'Transportation Management', description: 'Transportation and logistics' }
]

const issueCategories = [
  { name: 'bug_fix', color: 'bg-red-100 text-red-800', description: 'Software bugs and errors' },
  { name: 'configuration', color: 'bg-blue-100 text-blue-800', description: 'System configuration issues' },
  { name: 'best_practice', color: 'bg-green-100 text-green-800', description: 'Recommended practices' },
  { name: 'workaround', color: 'bg-yellow-100 text-yellow-800', description: 'Temporary solutions' },
  { name: 'integration', color: 'bg-purple-100 text-purple-800', description: 'Integration problems' },
  { name: 'performance', color: 'bg-orange-100 text-orange-800', description: 'Performance optimization' },
  { name: 'migration', color: 'bg-indigo-100 text-indigo-800', description: 'Migration issues' },
  { name: 'other', color: 'bg-gray-100 text-gray-800', description: 'Miscellaneous' }
]

const priorityLevels = [
  { name: 'critical', color: 'bg-red-500 text-white', description: 'Immediate attention required' },
  { name: 'high', color: 'bg-orange-500 text-white', description: 'High priority' },
  { name: 'medium', color: 'bg-yellow-500 text-white', description: 'Normal priority' },
  { name: 'low', color: 'bg-green-500 text-white', description: 'Low priority' }
]

const userRoles = [
  { name: 'system_admin', description: 'Full system access' },
  { name: 'org_admin', description: 'Organization administrator' },
  { name: 'expert', description: 'Subject matter expert' },
  { name: 'analyst', description: 'Data analyst' },
  { name: 'user', description: 'Standard user' }
]

const issueStatuses = [
  { name: 'draft', color: 'bg-gray-100 text-gray-800' },
  { name: 'open', color: 'bg-blue-100 text-blue-800' },
  { name: 'assigned', color: 'bg-purple-100 text-purple-800' },
  { name: 'in_progress', color: 'bg-yellow-100 text-yellow-800' },
  { name: 'resolved', color: 'bg-green-100 text-green-800' },
  { name: 'closed', color: 'bg-gray-300 text-gray-800' },
  { name: 'archived', color: 'bg-gray-200 text-gray-600' }
]

function AttributeGroup({ title, items, renderItem }: {
  title: string
  items: any[]
  renderItem: (item: any, index: number) => React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
      <h3 className="mb-4 text-lg font-semibold text-kaycoo-teal-dark">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => renderItem(item, index))}
      </div>
    </div>
  )
}

export function AttributeSection() {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Tag className="h-6 w-6 text-kaycoo-teal-dark" />
          <h2 className="text-2xl font-bold text-kaycoo-teal-dark">Domain Attributes</h2>
        </div>
        <p className="text-kaycoo-charcoal-2">
          D365 F&O specific attributes, system enumerations, and configuration values.
        </p>
      </div>

      {/* D365 Versions */}
      <AttributeGroup
        title="Supported D365 F&O Versions"
        items={d365Versions}
        renderItem={(version) => (
          <Badge key={version} className="bg-kaycoo-teal text-white">
            {version}
          </Badge>
        )}
      />

      {/* D365 Modules */}
      <AttributeGroup
        title="D365 F&O Modules"
        items={d365Modules}
        renderItem={(module) => (
          <div key={module.name} className="rounded border border-kaycoo-teal bg-kaycoo-teal-pale p-2">
            <div className="font-medium text-kaycoo-teal-dark">{module.name}</div>
            <div className="text-xs text-kaycoo-charcoal-2">{module.description}</div>
          </div>
        )}
      />

      {/* Issue Categories */}
      <AttributeGroup
        title="Issue Categories"
        items={issueCategories}
        renderItem={(category) => (
          <div key={category.name} className={`rounded px-3 py-1 ${category.color}`}>
            <div className="font-medium">{category.name}</div>
            <div className="text-xs opacity-80">{category.description}</div>
          </div>
        )}
      />

      {/* Priority Levels */}
      <AttributeGroup
        title="Priority Levels"
        items={priorityLevels}
        renderItem={(priority) => (
          <div key={priority.name} className={`rounded px-3 py-1 ${priority.color}`}>
            <div className="font-medium uppercase">{priority.name}</div>
            <div className="text-xs opacity-90">{priority.description}</div>
          </div>
        )}
      />

      {/* User Roles */}
      <AttributeGroup
        title="User Roles"
        items={userRoles}
        renderItem={(role) => (
          <div key={role.name} className="rounded border border-kaycoo-teal bg-white p-2">
            <div className="font-mono text-sm font-medium text-kaycoo-teal-dark">{role.name}</div>
            <div className="text-xs text-kaycoo-charcoal-2">{role.description}</div>
          </div>
        )}
      />

      {/* Issue Statuses */}
      <AttributeGroup
        title="Issue Status Workflow"
        items={issueStatuses}
        renderItem={(status, index) => (
          <div key={status.name} className="flex items-center gap-2">
            <div className={`rounded px-3 py-1 ${status.color}`}>
              {status.name}
            </div>
            {index < issueStatuses.length - 1 && (
              <span className="text-kaycoo-charcoal-2">→</span>
            )}
          </div>
        )}
      />
    </div>
  )
}
